import config from '../../../config';

import {
  ACE_WEB_LOCAL_CLIENT_ID,
  CROSS_FLAG_KEY_PREFIX,
  DISABLED_FLAG,
  FLAG_ENVIRONMENT_BY_CURRENT_ENV,
  FLAG_ENVIRONMENTS,
  FLAG_KEY_PREFIX,
} from './constants';
import {
  BffFlag,
  FeatureFlagMap,
  FeatureFlags,
  FeatureFlagValue,
  FlagEnvironment,
  FlagStreamEvent,
  RequestedFeatureFlags,
} from './types';

export const resolveFlagEnvironment = (): FlagEnvironment => {
  const configured = config.flagEnvironment?.toUpperCase() ?? '';
  if (FLAG_ENVIRONMENTS.has(configured)) {
    return configured as FlagEnvironment;
  }

  if (config.auth.clientId?.toLowerCase() === ACE_WEB_LOCAL_CLIENT_ID) {
    return FlagEnvironment.LOCAL;
  }

  return FLAG_ENVIRONMENT_BY_CURRENT_ENV[config.currentEnv] ?? FlagEnvironment.DEV;
};

const CROSS_FLAGS = new Set<string>(
  Object.values(FeatureFlags).filter(flag => flag.startsWith(CROSS_FLAG_KEY_PREFIX)),
);

/**
 * `ace-web.maintenance` -> `maintenance`. Flags of any other service are not ours.
 * A known `cross.` flag keeps its exact key; `ace-web.cross.x` is not ours and is dropped.
 */
const toFeatureFlagName = (key: string): string | null => {
  if (CROSS_FLAGS.has(key)) return key;
  if (!key?.startsWith(FLAG_KEY_PREFIX)) return null;

  const name = key.slice(FLAG_KEY_PREFIX.length);
  return name && !CROSS_FLAGS.has(name) ? name : null;
};

/** An empty payload carries no information, so it is stored as `null`. */
const toFlagValue = (flag: Partial<BffFlag>): FeatureFlagValue => ({
  enabled: Boolean(flag.enabled),
  value: flag.payload && Object.keys(flag.payload).length > 0 ? flag.payload : null,
});

export const toFeatureFlagMap = (flags: Record<string, BffFlag> = {}): FeatureFlagMap => (
  Object.entries(flags).reduce<FeatureFlagMap>((acc, [ key, flag ]) => {
    const name = toFeatureFlagName(key);

    if (name) acc[name] = toFlagValue(flag);

    return acc;
  }, {} as FeatureFlagMap)
);

export const applyFlagEvent = (flags: FeatureFlagMap, event: FlagStreamEvent): FeatureFlagMap => {
  const name = toFeatureFlagName(event.key);

  return name ? { ...flags, [name]: toFlagValue(event) } : flags;
};

export const parseFlagStreamEvent = (rawData: string): FlagStreamEvent | null => {
  try {
    const event = JSON.parse(rawData);
    return typeof event?.key === 'string' ? event : null;
  } catch (error) {
    return null;
  }
};

export const isFlagEnabled = (flags: FeatureFlagMap, flag: FeatureFlags): boolean => (
  flags[flag]?.enabled === true
);

/** Fills in the flags a caller asked for so consumers can read `.enabled` without a guard. */
export const pickRequestedFlags = (
  flags: FeatureFlagMap,
  requested: FeatureFlags[],
): RequestedFeatureFlags => requested.reduce(
  (acc, flag) => ({ ...acc, [flag]: flags[flag] ?? DISABLED_FLAG }),
  {} as RequestedFeatureFlags,
);
